import type { BaseItemSchema } from "@memorang/types/src/session/new-session";
import type { IconType } from "@memorang/ui";
import type { ModuleItemStats } from "./proethica-module-context";

export type ProethicaStatTileKey =
	| "module"
	| "activities"
	| "timeTaken"
	| "knowledgeCheck"
	| "questions"
	| "avgPerQuestion";

export type ProethicaStatValue =
	| { state: "loading" }
	| { state: "absent" }
	| { state: "count"; count: number }
	| { state: "minutes"; minutes: number }
	| { state: "duration"; seconds: number }
	| { state: "score"; numCorrect: number; numTotal: number };

// The module report reports time rounded to whole minutes; the test report needs
// second-level precision (average per question is a handful of seconds), so this
// renders a compact "1m 37s" / "3s" instead.
export const formatProethicaDurationSeconds = (seconds: number): string => {
	const safe = Math.max(0, Math.round(seconds));
	const minutes = Math.floor(safe / 60);
	const remainder = safe % 60;
	if (minutes === 0) {
		return `${remainder}s`;
	}
	if (remainder === 0) {
		return `${minutes}m`;
	}
	return `${minutes}m ${remainder}s`;
};

export type ProethicaStatTile = {
	key: ProethicaStatTileKey;
	icon: IconType;
	value: ProethicaStatValue;
};

export type ProethicaStatsInput = {
	moduleNumber?: number;
	activityCount?: number;
	sessionItems: BaseItemSchema[];
	statsById: Map<number, ModuleItemStats>;
	loading: boolean;
	wallClockSeconds?: number;
	persistedWallClockSeconds?: number;
};

const FULL_MARKS = 100;

const isKnowledgeCheckItem = (item: BaseItemSchema) =>
	item.metadata?.type === "MCSS";

export const sumTimeTakenSeconds = (
	statsById: Map<number, ModuleItemStats>,
): number | undefined => {
	let total = 0;
	let tracked = false;
	for (const { timeTaken } of statsById.values()) {
		if (typeof timeTaken === "number") {
			total += timeTaken;
			tracked = true;
		}
	}
	return tracked ? total : undefined;
};

// Partial credit is not a pass: only a full-mark item counts toward the
// numerator. An item the stats map never scored (skipped, or a module with no
// knowledge check) leaves the tally absent rather than reporting a false zero.
export const tallyKnowledgeCheck = (
	sessionItems: BaseItemSchema[],
	statsById: Map<number, ModuleItemStats>,
): { numCorrect: number; numTotal: number } | undefined => {
	const items = sessionItems.filter(isKnowledgeCheckItem);
	if (!items.length) {
		return undefined;
	}
	const scores = items
		.map((item) =>
			item.id == null ? undefined : statsById.get(item.id)?.score,
		)
		.filter((score): score is number => score != null);
	if (!scores.length) {
		return undefined;
	}
	return {
		numCorrect: scores.filter((score) => score >= FULL_MARKS).length,
		numTotal: items.length,
	};
};

const countValue = (count?: number): ProethicaStatValue =>
	count == null ? { state: "absent" } : { state: "count", count };

const positive = (seconds?: number): number | undefined =>
	seconds != null && seconds > 0 ? seconds : undefined;

// The backend per-item sum is the authoritative, cross-device source, but it is
// zero/absent for content-only (and AI-scenario-only) modules that post no timed
// events. There it degrades to the locally tracked session wall clock: the live
// store's in-memory tick first, then the per-session persisted value that
// survives a same-device refresh. A zero-or-less stats sum is treated as absent
// so a "0m" never wins over a real tracked time.
export const resolveModuleTimeSeconds = ({
	statsSumSeconds,
	wallClockSeconds,
	persistedWallClockSeconds,
}: {
	statsSumSeconds?: number;
	wallClockSeconds?: number;
	persistedWallClockSeconds?: number;
}): number | undefined =>
	positive(statsSumSeconds) ??
	positive(wallClockSeconds) ??
	positive(persistedWallClockSeconds);

const timeTakenValue = (
	statsById: Map<number, ModuleItemStats>,
	loading: boolean,
	wallClockSeconds?: number,
	persistedWallClockSeconds?: number,
): ProethicaStatValue => {
	if (loading) {
		return { state: "loading" };
	}
	const seconds = resolveModuleTimeSeconds({
		statsSumSeconds: sumTimeTakenSeconds(statsById),
		wallClockSeconds,
		persistedWallClockSeconds,
	});
	return seconds == null
		? { state: "absent" }
		: { state: "minutes", minutes: Math.round(seconds / 60) };
};

const knowledgeCheckValue = (
	sessionItems: BaseItemSchema[],
	statsById: Map<number, ModuleItemStats>,
	loading: boolean,
): ProethicaStatValue => {
	if (loading) {
		return { state: "loading" };
	}
	const tally = tallyKnowledgeCheck(sessionItems, statsById);
	return tally ? { state: "score", ...tally } : { state: "absent" };
};

// The grid is fixed at four tiles: an unavailable stat degrades to a placeholder
// so the row never collapses.
export const buildProethicaStatTiles = ({
	moduleNumber,
	activityCount,
	sessionItems,
	statsById,
	loading,
	wallClockSeconds,
	persistedWallClockSeconds,
}: ProethicaStatsInput): ProethicaStatTile[] => [
	{
		key: "module",
		icon: "book-open-variant",
		value: countValue(moduleNumber),
	},
	{
		key: "activities",
		icon: "check-circle-outline",
		value: countValue(activityCount),
	},
	{
		key: "timeTaken",
		icon: "clock-outline",
		value: timeTakenValue(
			statsById,
			loading,
			wallClockSeconds,
			persistedWallClockSeconds,
		),
	},
	{
		key: "knowledgeCheck",
		icon: "clipboard-check-outline",
		value: knowledgeCheckValue(sessionItems, statsById, loading),
	},
];
